import type { PersonSummary } from '@feral/shared/client'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { Fragment, useEffect } from 'react'
import { usePrefs } from '../../lib/prefs.js'
import { useSession } from '../../lib/session.js'
import { play } from '../../lib/sounds.js'
import { socket } from '../../lib/ws.js'
import { StatusText, UserIcon } from '../people/UserIcon.js'
import { presenceWords, statusKind, statusWords } from '../people/words.js'
import { WALL_KEY } from '../wall/seen.js'
import { useChat } from './store.js'

/**
 * A person on an inbox row or a thread header, with the SFW rule applied (FR-SFW-02), read as every user
 * icon is (docs/USER-ICONS.md): presence ring, tier line, status, role, position, your warning.
 */
export function PersonAvatar({ p, size = 44 }: { p: PersonSummary; size?: number }) {
  const sfw = usePrefs((s) => s.sfw)
  return (
    <UserIcon
      size={size}
      s={
        p.gone
          ? { ring: 'stale', tier: 'trusted', photo: null, status: null, position: null }
          : {
              ring: p.ring,
              tier: p.tier,
              photo: sfw ? p.sfw : p.photo,
              status: statusKind(p.status),
              position: p.position,
              sucking: p.sucking,
              id: p.id,
            }
      }
    />
  )
}

/**
 * When they were on and what they're up to, in the words and colors used everywhere (words.ts), then
 * whatever the row adds (a distance, a day).
 */
export function PersonLine({ p, more = [] }: { p: PersonSummary; more?: (string | null | false)[] }) {
  if (p.gone) return <>No longer on Feral Mode.</>
  const line = [
    presenceWords({ ring: p.ring, lastActive: p.lastActive }),
    statusWords(p.status) ? <StatusText key="status" s={p.status} long /> : null,
    ...more,
  ].filter(Boolean)
  return (
    <>
      {line.map((w) => (
        <Fragment key={typeof w === 'string' ? w : 'status'}>
          {w !== line[0] && ' · '}
          {w}
        </Fragment>
      ))}
    </>
  )
}

export const timeAgo = (iso: string) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (m < 1) return 'now'
  if (m < 60) return `${m}m`
  if (m < 24 * 60) return `${Math.round(m / 60)}h`
  return `${Math.round(m / (24 * 60))}d`
}
export const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

/**
 * Someone's status changed: their profile, the threads and lists that show them, and the Wall, so no
 * copy of them keeps the old one while the map shows the new (bugs.md). The map's own pins refetch on
 * the cell's `pin.update`, which never says who (it would place people a viewer can't see); the map
 * compares its replies and calls this for whoever changed.
 */
export function refreshPeople(qc: QueryClient, ids: readonly string[]) {
  if (!ids.length) return
  for (const id of ids) qc.invalidateQueries({ queryKey: ['user', id] })
  qc.invalidateQueries({ queryKey: ['conversations'] })
  qc.invalidateQueries({ queryKey: ['conversation'] })
  qc.invalidateQueries({ queryKey: ['favorites'] })
  qc.invalidateQueries({ queryKey: WALL_KEY })
}

/** Keep the inbox and open threads fresh from the socket; chirp on a new message when sounds are on. */
export function useChatSocket() {
  const qc = useQueryClient()
  const signedIn = useSession((s) => !!s.me)
  useEffect(() => {
    if (!signedIn) return
    socket.connect()
    return socket.on((f) => {
      if (f.t === 'message.new' || f.t === 'message.reaction') {
        qc.invalidateQueries({ queryKey: ['conversation', f.conversationId] })
        qc.invalidateQueries({ queryKey: ['conversations'] })
        qc.invalidateQueries({ queryKey: ['inbox', 'counts'] })
        if (usePrefs.getState().sounds) chirp()
      } else if (f.t === 'message.state' || f.t === 'conversation.update') {
        qc.invalidateQueries({ queryKey: ['conversation'] })
        qc.invalidateQueries({ queryKey: ['conversations'] })
        qc.invalidateQueries({ queryKey: ['inbox', 'counts'] })
      } else if (f.t === 'tap.new') {
        qc.invalidateQueries({ queryKey: ['taps'] })
        qc.invalidateQueries({ queryKey: ['knocks'] })
        qc.invalidateQueries({ queryKey: ['inbox', 'counts'] })
        if (usePrefs.getState().sounds) chirp()
      } else if (f.t === 'status.update') {
        // Your own status, or a favorite's you asked to hear about: every copy of them follows.
        refreshPeople(qc, [f.userId])
      } else if (f.t === 'typing') {
        useChat.getState().setTyping(f.conversationId)
      } else if (f.t === 'profile.revoked') {
        // Their card in the thread, and their profile if it's open: back to what you'd see without it.
        qc.invalidateQueries({ queryKey: ['conversation'] })
        qc.invalidateQueries({ queryKey: ['user', f.userId] })
      } else if (f.t === 'socials.revoked') {
        // Their socials, wherever you saw them: the thread's card, their profile, your Favorites.
        qc.invalidateQueries({ queryKey: ['conversation'] })
        qc.invalidateQueries({ queryKey: ['user', f.userId] })
        qc.invalidateQueries({ queryKey: ['favorites'] })
      } else if (f.t === 'album.revoked') {
        // The thread's album card, and the album itself on their profile or open in a sheet.
        qc.invalidateQueries({ queryKey: ['conversation'] })
        qc.invalidateQueries({ queryKey: ['user'] })
      }
    })
  }, [qc, signedIn])
}

/** The sound for a new message or tap: the one picked on this device (lib/sounds.ts). */
export const chirp = () => play(usePrefs.getState().sound)
